/* Predictive search with autocomplete (header and search page box) + the search results page,
   and the in-browser pager used by collection pages.
   One matching rule everywhere: every typed word must appear in the product's search text
   (snippets/search-text.liquid), ignoring case and punctuation, so "toy" finds Toyota, "crv" finds
   CR-V and "1153" or "#1153" finds that stock number.
   Order of results: an exact stock-number match first, then in-stock products before sold-out
   ones, then newest stock numbers first.
   Product data: /collections/all?view=search-index (templates/collection.search-index.liquid),
   kept for 5 minutes per browser tab.
   Keys: Up/Down move through the list (a highlighted suggestion is written into the box),
   Enter opens it, Tab or Right arrow accepts the grey autocomplete text, Esc closes. */
(function () {
  'use strict';

  var cfg = (window.theme && window.theme.search) || {};
  var STR = cfg.strings || {};
  var INDEX_URL = cfg.indexUrl || '/collections/all?view=search-index';
  var SEARCH_URL = cfg.url || '/search';
  var CACHE_KEY = 'jeb-search-index:' + INDEX_URL;
  var TTL = 5 * 60 * 1000;
  var MAX_TERMS = 6;
  var MAX_CATS = 3;
  var MAX_PRODUCTS = 6;
  /* accents (U+0300 to U+036F), built from char codes so the file has no escape sequences */
  var ACCENTS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

  var ICON_SEARCH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
  var ICON_CATEGORY = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>';
  var ICON_ENGINE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22"><path d="M7 8h8l2 3h2v6h-2l-2 2H8l-2-3H4v-5h3z"/><path d="M10 5h4M12 5v3"/></svg>';

  /* ---------- matching ---------- */
  function norm(s) {
    s = String(s == null ? '' : s).toLowerCase();
    return s.normalize ? s.normalize('NFD').replace(ACCENTS, '') : s;
  }
  function spaced(s) { return norm(s).replace(/[^a-z0-9#]+/g, ' ').trim(); }
  function joined(s) { return norm(s).replace(/[^a-z0-9]+/g, ''); }
  function words(q) { var w = spaced(q); return w ? w.split(' ') : []; }
  function prep(text) { return { s: ' ' + spaced(text) + ' ', j: joined(text) }; }
  function hit(m, ws) {
    for (var i = 0; i < ws.length; i++) {
      var w = ws[i];
      if (m.s.indexOf(w) !== -1) continue;
      var jw = w.replace(/#/g, '');
      if (jw && m.j.indexOf(jw) !== -1) continue;
      return false;
    }
    return true;
  }
  function skuNum(s) {
    var n = parseInt(String(s == null ? '' : s).replace(/^#/, ''), 10);
    return isNaN(n) ? -1 : n;
  }
  function exactSku(q) { return spaced(q).replace(/^#/, ''); }
  /* 1 when the product can be bought, 0 when it is sold out */
  function inStock(p) { return p.a ? 1 : 0; }
  function byRelevance(exact) {
    return function (a, b) {
      var ea = String(a.s || '').toLowerCase() === exact ? 1 : 0;
      var eb = String(b.s || '').toLowerCase() === exact ? 1 : 0;
      return (eb - ea) || (inStock(b) - inStock(a)) || (b._n - a._n) || ((b.d || 0) - (a.d || 0));
    };
  }
  function findProducts(products, q) {
    var ws = words(q);
    if (!ws.length) return [];
    return products.filter(function (p) { return hit(p._m, ws); }).sort(byRelevance(exactSku(q)));
  }

  /* ---------- product index ---------- */
  var indexPromise = null;

  function build(data) {
    var products = (data && data.products || []).map(function (p) {
      p._m = prep((p.t || '') + ' ' + (p.k || ''));
      p._n = skuNum(p.s);
      p.d = +p.d || 0;
      return p;
    });
    var seen = {};
    var terms = [];
    function add(label) {
      label = String(label == null ? '' : label).replace(/\s+/g, ' ').trim();
      var key = spaced(label);
      if (key.length < 2 || seen[key]) return;
      seen[key] = true;
      terms.push({ label: label, key: key });
    }
    products.forEach(function (p) {
      add(p.v);
      String(p.m || '').split(/\s*[\/,|&]\s*/).forEach(function (m) {
        if (!m) return;
        add(p.v && norm(m).indexOf(norm(p.v)) !== 0 ? p.v + ' ' + m : m);
      });
      String(p.e || '').split(/\s*[\/,|&]\s*/).forEach(add);
      add(p.pt);
    });
    var cats = (data && data.categories || []).filter(function (c) { return c && c.t && c.u; });
    cats.forEach(function (c) { c._m = prep(c.t); add(c.t); });
    terms = terms.filter(function (t) { return !seen[t.key + 's']; }); /* "Engine" + "Engines" -> keep "Engines" */
    terms.forEach(function (t) { t.n = findProducts(products, t.label).length; });
    terms = terms.filter(function (t) { return t.n > 0; });
    return { products: products, terms: terms, cats: cats };
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.data && Date.now() - cached.at < TTL) {
        indexPromise = Promise.resolve(build(cached.data));
        return indexPromise;
      }
    } catch (e) { /* storage unavailable */ }
    indexPromise = fetch(INDEX_URL, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Search index ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: data })); } catch (e) { /* full or blocked */ }
        return build(data);
      })
      .catch(function (err) {
        indexPromise = null;
        throw err;
      });
    return indexPromise;
  }

  /* ---------- suggestions (autocomplete) ---------- */
  function suggest(idx, q) {
    var nq = spaced(q);
    if (!nq) return [];
    var out = [];
    idx.terms.forEach(function (t) {
      var rank = -1;
      if (t.key === nq) rank = 0;
      else if (t.key.indexOf(nq) === 0) rank = 1;
      else if ((' ' + t.key).indexOf(' ' + nq) !== -1) rank = 2;
      if (rank > -1) out.push({ label: t.label, n: t.n, rank: rank });
    });
    out.sort(function (a, b) { return (a.rank - b.rank) || (b.n - a.n) || (a.label.length - b.label.length); });
    return out.slice(0, MAX_TERMS);
  }
  function popular(idx) {
    return idx.terms.slice().sort(function (a, b) { return b.n - a.n; }).slice(0, MAX_TERMS);
  }

  /* ---------- rendering helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(str, map) {
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  function mark(text, q) {
    text = String(text == null ? '' : text);
    var lower = text.toLowerCase();
    var ranges = [];
    words(q).forEach(function (w) {
      var i = lower.indexOf(w);
      if (i !== -1) ranges.push([i, i + w.length]);
    });
    if (!ranges.length) return esc(text);
    ranges.sort(function (a, b) { return a[0] - b[0]; });
    var html = '';
    var pos = 0;
    ranges.forEach(function (r) {
      if (r[0] < pos) return;
      html += esc(text.slice(pos, r[0])) + '<mark>' + esc(text.slice(r[0], r[1])) + '</mark>';
      pos = r[1];
    });
    return html + esc(text.slice(pos));
  }
  function searchHref(q) {
    return SEARCH_URL + (SEARCH_URL.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(q);
  }
  function group(title, rows) {
    return '<div class="ps__group" role="group" aria-label="' + esc(title) + '"><p class="ps__heading">' + esc(title) + '</p>' + rows + '</div>';
  }
  function termRow(t, q) {
    return '<a class="ps__item ps__item--term" role="option" data-ps-opt data-ps-term="' + esc(t.label) + '" href="' + esc(searchHref(t.label)) + '">' +
      '<span class="ps__icon">' + ICON_SEARCH + '</span>' +
      '<span class="ps__term">' + mark(t.label, q) + '</span>' +
      '<span class="ps__count">' + t.n + '</span></a>';
  }
  function catRow(c, q) {
    return '<a class="ps__item" role="option" data-ps-opt href="' + esc(c.u) + '">' +
      '<span class="ps__icon">' + ICON_CATEGORY + '</span>' +
      '<span class="ps__term">' + mark(c.t, q) + '</span></a>';
  }
  function productRow(p, q) {
    var thumb = p.i ? '<img src="' + esc(p.i) + '" alt="" width="80" height="60" loading="lazy">' : ICON_ENGINE;
    var meta = [];
    if (p.s) meta.push('#' + esc(p.s));
    if (p.v) meta.push(esc(p.v));
    return '<a class="ps__item ps__item--product" role="option" data-ps-opt href="' + esc(p.u) + '">' +
      '<span class="ps__thumb">' + thumb + '</span>' +
      '<span class="ps__info"><span class="ps__title">' + mark(p.t, q) + '</span>' +
      '<span class="ps__meta">' + meta.join(' &middot; ') + (p.a ? '' : '<span class="ps__sold">' + esc(STR.soldOut || 'Sold out') + '</span>') + '</span></span>' +
      '<span class="ps__price">' + esc(p.p) + (p.c ? '<s>' + esc(p.c) + '</s>' : '') + '</span></a>';
  }

  /* ---------- one search box ---------- */
  function setup(form) {
    if (form.getAttribute('data-ps-ready')) return;
    form.setAttribute('data-ps-ready', 'true');
    var input = form.querySelector('[data-ps-input]');
    var panel = form.querySelector('[data-ps-panel]');
    var ghost = form.querySelector('[data-ps-ghost]');
    if (!input || !panel) return;

    var uid = input.id || 'ps' + Math.random().toString(36).slice(2, 8);
    var typed = input.value;
    var opts = [];
    var active = -1;
    var completion = '';
    var timer = null;
    var seq = 0;

    function isOpen() { return !panel.hidden; }
    function open() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      form.classList.add('is-open');
    }
    function close() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      form.classList.remove('is-open');
      if (active > -1 && opts[active]) opts[active].classList.remove('is-active');
      active = -1;
      setGhost('');
    }
    function atEnd() {
      try { return input.selectionStart === input.value.length && input.selectionEnd === input.value.length; } catch (e) { return true; }
    }
    function setGhost(label) {
      completion = '';
      if (!ghost) return;
      var v = input.value;
      if (!label || !v || document.activeElement !== input || label.toLowerCase().indexOf(v.toLowerCase()) !== 0 || label.length <= v.length) {
        ghost.innerHTML = '';
        return;
      }
      completion = label;
      ghost.innerHTML = '<span class="ps__ghost-typed">' + esc(v) + '</span>' + esc(label.slice(v.length));
      if (input.scrollWidth > input.clientWidth + 1) { ghost.innerHTML = ''; completion = ''; }
    }
    function setActive(i) {
      if (active > -1 && opts[active]) opts[active].classList.remove('is-active');
      active = i;
      if (i > -1 && opts[i]) {
        var o = opts[i];
        o.classList.add('is-active');
        if (o.scrollIntoView) o.scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', o.id);
        var term = o.getAttribute('data-ps-term');
        input.value = term != null ? term : typed;
        setGhost('');
      } else {
        input.removeAttribute('aria-activedescendant');
        input.value = typed;
      }
    }

    function render(idx, q) {
      var trimmed = q.replace(/^\s+/, '');
      var html = '';
      var top = '';
      if (!trimmed.trim()) {
        var pop = popular(idx);
        if (pop.length) html = group(STR.popular || 'Popular searches', pop.map(function (t) { return termRow(t, ''); }).join(''));
      } else {
        var terms = suggest(idx, trimmed);
        var ws = words(trimmed);
        var cats = idx.cats.filter(function (c) { return hit(c._m, ws); }).slice(0, MAX_CATS);
        var prods = findProducts(idx.products, trimmed);
        if (terms.length) html += group(STR.suggestions || 'Suggestions', terms.map(function (t) { return termRow(t, trimmed); }).join(''));
        if (cats.length) html += group(STR.categories || 'Categories', cats.map(function (c) { return catRow(c, trimmed); }).join(''));
        if (prods.length) {
          html += group(STR.products || 'Products', prods.slice(0, MAX_PRODUCTS).map(function (p) { return productRow(p, trimmed); }).join(''));
          html += '<a class="ps__all" role="option" data-ps-opt href="' + esc(searchHref(trimmed.trim())) + '">' +
            esc(fmt(STR.viewAll || 'View all [count] results for "[terms]"', { count: prods.length, terms: trimmed.trim() })) + ' &rarr;</a>';
        } else if (!terms.length && !cats.length) {
          html += '<p class="ps__empty">' + esc(fmt(STR.noResultsFor || 'No results for "[terms]"', { terms: trimmed.trim() })) + '</p>';
        }
        for (var i = 0; i < terms.length; i++) {
          if (terms[i].label.toLowerCase().indexOf(q.toLowerCase()) === 0 && terms[i].label.length > q.length) { top = terms[i].label; break; }
        }
      }
      if (!html) { close(); return; }
      panel.innerHTML = html;
      opts = [].slice.call(panel.querySelectorAll('[data-ps-opt]'));
      opts.forEach(function (o, k) { o.id = uid + '-opt-' + k; });
      active = -1;
      input.removeAttribute('aria-activedescendant');
      open();
      setGhost(top);
    }

    function update() {
      var my = ++seq;
      var q = input.value;
      loadIndex().then(function (idx) {
        if (my !== seq || document.activeElement !== input) return;
        render(idx, q);
      }, function () {
        if (my === seq) close();
      });
    }
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(update, 60);
    }

    input.addEventListener('input', function () {
      typed = input.value;
      active = -1;
      setGhost('');
      schedule();
    });
    input.addEventListener('focus', function () {
      typed = input.value;
      update();
    });
    input.addEventListener('pointerenter', function () { loadIndex().catch(function () {}); }, { once: true });
    input.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowDown' || k === 'ArrowUp') {
        if (!isOpen()) { update(); return; }
        e.preventDefault();
        if (!opts.length) return;
        var next = active + (k === 'ArrowDown' ? 1 : -1);
        if (next < -1) next = opts.length - 1;
        if (next >= opts.length) next = -1;
        setActive(next);
      } else if (k === 'Enter') {
        if (isOpen() && active > -1 && opts[active]) {
          e.preventDefault();
          window.location.href = opts[active].href;
        }
      } else if (k === 'Escape') {
        if (isOpen()) {
          e.preventDefault();
          input.value = typed;
          close();
        }
      } else if ((k === 'Tab' && !e.shiftKey) || k === 'ArrowRight') {
        if (completion && atEnd()) {
          e.preventDefault();
          input.value = completion;
          typed = completion;
          setGhost('');
          schedule();
        }
      }
    });
    input.addEventListener('blur', function () { setGhost(''); });
    form.addEventListener('focusout', function (e) {
      if (e.relatedTarget && !form.contains(e.relatedTarget)) close();
    });
    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
    form.addEventListener('submit', function () {
      clearTimeout(timer);
      close();
    });
    window.addEventListener('pageshow', function () { close(); });
  }

  /* ---------- search results page + collection pages ---------- */
  /* Both render every product once (sections/main-search.liquid, sections/main-collection.liquid)
     and page through them here. The page number lives in ?pg= (Shopify's own ?page= would page
     the product list the section renders).
     - Search page ([data-search-page]): keeps the products matching ?q=, ordered by byRelevance.
     - Collection page ([data-search-page][data-collection-page]): keeps the order Shopify
       rendered (the collection's sort order or the customer's "Sort by" choice) but moves every
       sold-out product after the in-stock ones. */
  var PAGE_PARAM = 'pg';

  /* Page numbers to show: first, last and two either side of the current page (one on phones).
     0 = gap (shown as an ellipsis); a gap of a single page shows that page instead. */
  function pageParts(cur, total, around) {
    var out = [];
    var last = 0;
    if (around == null) around = 2;
    for (var i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= cur - around && i <= cur + around)) {
        if (last && i - last === 2) out.push(i - 1);
        else if (last && i - last > 2) out.push(0);
        out.push(i);
        last = i;
      }
    }
    return out;
  }

  function itemAvailable(el) {
    var a = el.getAttribute('data-a');
    if (a != null) return a === '1' || a === 'true';
    return !el.querySelector('.product-card.is-sold-out');
  }

  function initResults(root) {
    var grid = root.querySelector('[data-search-grid]');
    if (!grid) return;
    var isCollection = root.hasAttribute('data-collection-page');
    var per = parseInt(root.getAttribute('data-per-page'), 10) || 12;
    var q = root.getAttribute('data-q') || '';
    var ws = words(q);
    var exact = exactSku(q);
    var items = [].slice.call(grid.querySelectorAll('[data-search-item]')).map(function (el) {
      var s = el.getAttribute('data-s') || '';
      return { el: el, s: s, _n: skuNum(s), d: +el.getAttribute('data-d') || 0, a: itemAvailable(el), m: prep(el.getAttribute('data-k') || '') };
    });
    var list;
    if (isCollection) {
      /* stable: in-stock products in their rendered order, then sold-out ones in theirs */
      list = items.filter(function (it) { return it.a; }).concat(items.filter(function (it) { return !it.a; }));
    } else {
      list = ws.length ? items.filter(function (it) { return hit(it.m, ws); }) : items.slice();
      list.sort(byRelevance(exact));
    }
    list.forEach(function (it) { grid.appendChild(it.el); });

    var pages = Math.max(1, Math.ceil(list.length / per));
    var nav = root.querySelector('[data-search-pagination]');
    var pageList = nav && nav.querySelector('[data-search-pages]');
    var info = nav && nav.querySelector('[data-search-info]');
    var top = root.querySelector('[data-search-top]') || root;
    function attr(name) { return (nav && nav.getAttribute(name)) || ''; }
    function iconHtml(sel) { var t = nav && nav.querySelector(sel); return t ? t.innerHTML : ''; }
    var L = {
      prev: attr('data-prev') || 'Previous',
      next: attr('data-next') || 'Next',
      showing: attr('data-showing') || 'Showing [from] to [to] of [total] results',
      label: attr('data-page-label') || 'Page [page]',
      iconPrev: iconHtml('[data-icon-prev]'),
      iconNext: iconHtml('[data-icon-next]')
    };

    function pageFromUrl() {
      var n = parseInt(new URLSearchParams(window.location.search).get(PAGE_PARAM), 10);
      return isNaN(n) || n < 1 ? 1 : Math.min(n, pages);
    }
    function urlFor(n) {
      var params = new URLSearchParams(window.location.search);
      if (n > 1) params.set(PAGE_PARAM, n); else params.delete(PAGE_PARAM);
      var s = params.toString();
      return window.location.pathname + (s ? '?' + s : '');
    }
    function link(n, html, extra) {
      return '<li><a href="' + esc(urlFor(n)) + '" data-page="' + n + '"' + (extra || '') + '>' + html + '</a></li>';
    }

    function render(n) {
      var from = (n - 1) * per;
      items.forEach(function (it) { it.el.classList.remove('is-shown'); });
      list.slice(from, from + per).forEach(function (it) { it.el.classList.add('is-shown'); });
      if (!nav) return;
      nav.hidden = pages < 2;
      if (pages < 2) return;
      var html = '';
      if (n > 1) html += link(n - 1, L.iconPrev + '<span>' + esc(L.prev) + '</span>', ' rel="prev" aria-label="' + esc(L.prev) + '"');
      var narrow = window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
      pageParts(n, pages, narrow ? 1 : 2).forEach(function (p) {
        if (!p) html += '<li><span class="is-gap" aria-hidden="true">&hellip;</span></li>';
        else if (p === n) html += '<li><span class="is-current" aria-current="page">' + p + '</span></li>';
        else html += link(p, String(p), ' aria-label="' + esc(fmt(L.label, { page: p })) + '"');
      });
      if (n < pages) html += link(n + 1, '<span>' + esc(L.next) + '</span>' + L.iconNext, ' rel="next" aria-label="' + esc(L.next) + '"');
      pageList.innerHTML = html;
      if (info) info.textContent = fmt(L.showing, { from: from + 1, to: Math.min(from + per, list.length), total: list.length });
    }
    function scrollToResults() {
      var header = document.querySelector('[data-header]');
      var offset = 0;
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') offset = header.offsetHeight;
      }
      var y = top.getBoundingClientRect().top + window.pageYOffset - offset - 16;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }

    var current = pageFromUrl();
    render(current);
    if (nav) {
      nav.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[data-page]');
        if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
        e.preventDefault();
        current = parseInt(a.getAttribute('data-page'), 10) || 1;
        window.history.pushState({ searchPage: current }, '', urlFor(current));
        render(current);
        scrollToResults();
      });
      window.addEventListener('popstate', function () {
        current = pageFromUrl();
        render(current);
      });
    }

    if (!isCollection) {
      var count = root.querySelector('[data-search-count]');
      if (count) count.textContent = fmt(list.length === 1 ? (STR.countOne || '[count] result found') : (STR.countOther || '[count] results found'), { count: list.length });
      var empty = root.querySelector('[data-search-empty]');
      if (empty) empty.hidden = list.length > 0;
      grid.hidden = list.length === 0;
    }
    root.classList.add('is-ready');
  }

  function init() {
    [].forEach.call(document.querySelectorAll('[data-predictive-search]'), setup);
    [].forEach.call(document.querySelectorAll('[data-search-page]'), initResults);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
